import { useCallback, useEffect } from 'react';
import {
  getAllAppointmentIframeAllowedOrigins,
  isMockAppointmentIframeEnabled,
} from 'common/env';

export const SESSION_KEEPALIVE_EVENT = 'session-keepalive';

/**
 * Keeps the parent-app session alive while an iframe modal is open.
 *
 * The League SessionTimeout component only resets its idle timer on
 * document-level click / keydown / visibilitychange events. Clicks inside
 * an iframe do not reach the parent document, so the iframe should
 * `postMessage` `{ type: 'session-keepalive' }` when it wants to reset the
 * parent idle timer (at whatever cadence the embed chooses). This hook
 * dispatches a synthetic `click` on `document` when that message is received.
 */
export function useIframeKeepAlive({ enabled }: { enabled: boolean }) {
  const keepAlive = useCallback(() => {
    document.dispatchEvent(new Event('click'));
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const allowedOrigins = getAllAppointmentIframeAllowedOrigins();

    if (!isMockAppointmentIframeEnabled()) {
      if (allowedOrigins.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          '[patient-portal] useIframeKeepAlive: neither ' +
            'VITE_AMPLAR_APPOINTMENT_IFRAME_ALLOWED_ORIGINS nor ' +
            'VITE_MY_HEALTH_APPOINTMENT_IFRAME_ALLOWED_ORIGINS is configured. ' +
            'Session keep-alive messages will be silently blocked. Set these to ' +
            'the iframe origin(s) to enable keep-alive.',
        );
      }
    }

    const handleMessage = (event: MessageEvent) => {
      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      if (
        typeof event.data === 'object' &&
        event.data !== null &&
        event.data.type === SESSION_KEEPALIVE_EVENT
      ) {
        keepAlive();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [enabled, keepAlive]);
}
